// F-096 Import de portefeuille : fichier CSV et archive ZIP facultative des pièces, avec aperçu, validation ligne par
// ligne, soldes de reprise, bilan des pièces et rapport d'erreurs. Rien n'est créé avant la confirmation.
import { useState } from "react";
import { post, upload } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, ErrorBox, FileDrop, Input, Loading, Modal, Notice, PageHeader, Select, Stat, Table } from "@core/ui";
import { Status, useRun } from "./shared";

const KIND_LABELS: Record<string, string> = { mandants: "Mandants", logements: "Logements", baux: "Baux", locataires: "Locataires" };

/** Premier jour du mois en cours (JJ/MM/AAAA), pour un exemple de date de reprise valide. */
function firstOfMonth(): string {
  const d = new Date();
  return `01/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function example(kind: string): string {
  switch (kind) {
    case "mandants":
      return "reference;type;nom;raison_sociale;siren;email;telephone\nM1;sci;SCI des Lilas;SCI des Lilas;123456782;sci@exemple.fr;";
    case "logements":
      return "reference;reference_mandant;type;libelle;adresse;code_postal;ville;surface_m2;pieces;meuble;fichiers;categorie_fichiers\nL1;M1;logement;T2 rue des Tanneurs;12 rue des Tanneurs;34000;Montpellier;42;2;non;L1/dpe.pdf;diagnostic";
    case "baux":
      return `reference;reference_logement;regime;date_debut;duree_mois;loyer;charges;mode_charges;depot;jour_paiement;date_reprise;solde_ouverture;motif_solde;depot_detenu;fichiers\nB1;L1;vide;15/03/2024;36;720,00;60,00;provision;720,00;5;${firstOfMonth()};350,00;Loyer du mois dernier impayé;720,00;"bail_signe:B1/bail.pdf;etat_des_lieux:B1/edl.pdf"`;
    default:
      return "reference_bail;nom_complet;email;telephone;role\nB1;Léa Martin;lea.martin@exemple.fr;;locataire";
  }
}

/** Explications des colonnes de reprise et de pièces. */
const COLUMN_HELP: [string, string][] = [
  ["date_reprise", "1er du mois à partir duquel Bailivo appelle les loyers (JJ/MM/AAAA). Les mois précédents restent ceux de votre outil actuel : aucune échéance n’est créée avant cette date."],
  ["solde_ouverture", "Solde du locataire la veille de la reprise, en euros (« 1 240,50 ») : positif s’il reste dû, négatif (« -120,00 ») pour un trop-perçu. Il est ajouté une seule fois à son compte, sans quittance."],
  ["motif_solde", "Justification du solde, par exemple « loyer de septembre impayé »."],
  ["depot_detenu", "Dépôt de garantie réellement détenu, s’il diffère du dépôt prévu au bail."],
  ["fichiers", "Fichiers de l’archive ZIP à rattacher, séparés par « ; » (entre guillemets) ou « | », éventuellement précédés de leur catégorie : bail_signe:B1/bail.pdf."],
  ["categorie_fichiers", "Catégorie des fichiers de la ligne qui n’en précisent pas."],
];

function downloadModel(kind: string) {
  const blob = new Blob([`\uFEFF${example(kind)}\n`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `modele-import-${kind}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const withPieces = (kind: string) => kind === "baux" || kind === "logements";

export function ImportPage() {
  const org = useOrg();
  const list = useGet<any>(org.api("/pro/imports"));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [kind, setKind] = useState("mandants");
  const [source, setSource] = useState("");
  const [csv, setCsv] = useState<File | null>(null);
  const [zip, setZip] = useState<File | null>(null);
  const [current, setCurrent] = useState<any>(null);
  const [open, setOpen] = useState<string | null>(null);
  const format = (list.data?.formats ?? []).find((f: any) => f.kind === kind);
  const toAttach = current?.summary?.pieces?.to_attach ?? 0;

  async function preview() {
    if (!csv) return;
    const f = new FormData();
    f.append("kind", kind);
    if (source.trim()) f.append("source", source.trim());
    f.append("file", csv);
    if (zip && withPieces(kind)) f.append("archive", zip);
    const r: any = await run(() => upload(org.api("/pro/imports"), f));
    if (r) {
      setCurrent(r);
      setCsv(null);
      setZip(null);
      invalidate(org.api("/pro/imports"));
    }
  }

  async function attachArchive(file: File) {
    const f = new FormData();
    f.append("file", file);
    const r: any = await run(() => upload(org.api(`/pro/imports/${current.import.id}/archive`), f), "Archive analysée.");
    if (r) {
      setCurrent(r);
      invalidate(org.api("/pro/imports"));
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Import de portefeuille"
        intro="Reprenez vos mandants, logements, baux, soldes, locataires et pièces depuis un fichier CSV et une archive ZIP. Chaque ligne et chaque pièce sont vérifiées, et vous voyez le résultat avant que quoi que ce soit ne soit créé."
      />
      <Notice title="Dans l’ordre : mandants, logements, baux, puis locataires">
        Chaque ligne porte une référence tirée de votre outil actuel. Une référence déjà importée n’est jamais recréée ni modifiée : relancer un import ne produit ni doublon ni nouvelle somme due, et
        rattache seulement les pièces encore absentes. L’import ne crée aucun appel de loyer : pour un bail en cours, indiquez la date de reprise et le solde d’ouverture ; les loyers démarrent à cette
        date et le solde est ajouté une seule fois au compte du locataire.
      </Notice>
      {org.can("operate") ? (
        <Card title="Déposer un fichier">
          <div className="grid-2">
            <Select
              label="Contenu du fichier"
              value={kind}
              options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))}
              onChange={(e) => {
                setKind(e.target.value);
                setZip(null);
              }}
            />
            <Input
              label="Outil d’origine (facultatif)"
              help="Nom du logiciel ou du tableur d’où viennent les données, conservé comme provenance."
              value={source}
              maxLength={80}
              onChange={(e) => setSource(e.target.value)}
            />
          </div>
          {format ? (
            <p className="small">
              Colonnes obligatoires : <span className="mono">{format.required.join(" ; ")}</span>
              <br />
              Colonnes facultatives : <span className="mono">{format.optional.join(" ; ")}</span>
            </p>
          ) : null}
          {withPieces(kind) ? (
            <details className="small">
              <summary>Reprise des soldes et rattachement des pièces</summary>
              <ul>
                {COLUMN_HELP.filter(([c]) => kind === "baux" || c === "fichiers" || c === "categorie_fichiers").map(([c, text]) => (
                  <li key={c}>
                    <span className="mono">{c}</span> : {text}
                  </li>
                ))}
              </ul>
              {format?.piece_categories?.length ? (
                <p>
                  Catégories de pièces :{" "}
                  {format.piece_categories.map((c: any, n: number) => (
                    <span key={c.code}>
                      {n ? " · " : ""}
                      <span className="mono">{c.code}</span> ({c.label})
                    </span>
                  ))}
                  .
                </p>
              ) : null}
            </details>
          ) : null}
          <p className="small muted">Exemple (séparateur point-virgule, montants en euros, dates JJ/MM/AAAA) :</p>
          <pre className="pro-secret small">{example(kind)}</pre>
          <div className="row">
            <Button variant="discret" small onClick={() => downloadModel(kind)}>
              Télécharger le modèle CSV
            </Button>
          </div>
          <FileDrop
            label="Choisir le fichier CSV"
            accept=".csv,text/csv,text/plain"
            help={csv ? `Fichier choisi : ${csv.name}` : "CSV UTF-8 (ou export Windows), 2 Mo et 5 000 lignes au plus."}
            onFiles={(files) => setCsv(files[0] ?? null)}
          />
          {withPieces(kind) ? (
            <FileDrop
              label="Choisir l’archive ZIP des pièces (facultatif)"
              accept=".zip,application/zip,application/x-zip-compressed"
              help={zip ? `Archive choisie : ${zip.name}` : "18 Mo au plus. Pièces acceptées : PDF, JPEG, PNG, WebP ou HEIC, 15 Mo chacune."}
              onFiles={(files) => setZip(files[0] ?? null)}
            />
          ) : null}
          <div className="row">
            <Button busy={busy} disabled={!csv} onClick={preview}>
              Vérifier le fichier
            </Button>
            {zip ? (
              <Button variant="discret" onClick={() => setZip(null)}>
                Retirer l’archive
              </Button>
            ) : null}
          </div>
          <ErrorBox error={error} />
        </Card>
      ) : null}
      {current ? (
        <Card title={`Aperçu — ${KIND_LABELS[current.import.kind]} (${current.import.filename})`}>
          {current.note ? <Notice tone="attention">{current.note}</Notice> : null}
          <ImportReport view={current} />
          {current.import.status === "apercu" ? (
            <div className="stack">
              {withPieces(current.import.kind) ? (
                <FileDrop
                  label={current.import.archive_filename ? "Remplacer l’archive des pièces" : "Ajouter l’archive des pièces"}
                  accept=".zip,application/zip,application/x-zip-compressed"
                  help="L’aperçu est recalculé avec la nouvelle archive."
                  busy={busy}
                  onFiles={(files) => files[0] && attachArchive(files[0])}
                />
              ) : null}
              <div className="row">
                <Button
                  busy={busy}
                  disabled={current.import.valid_rows === 0 && toAttach === 0}
                  onClick={async () => {
                    const r: any = await run(() => post(org.api(`/pro/imports/${current.import.id}/apply`)), "Import appliqué.");
                    if (r) {
                      setCurrent(r);
                      invalidate(org.api("/pro"));
                    }
                  }}
                >
                  {`Importer les ${current.import.valid_rows} ligne(s) valide(s)${toAttach ? ` et ${toAttach} pièce(s)` : ""}`}
                </Button>
                <Button
                  variant="discret"
                  onClick={async () => {
                    if (await run(() => post(org.api(`/pro/imports/${current.import.id}/cancel`)), "Aperçu abandonné.")) {
                      setCurrent(null);
                      invalidate(org.api("/pro/imports"));
                    }
                  }}
                >
                  Abandonner
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}
      <Card title="Historique des imports">
        {list.isLoading ? <Loading /> : null}
        {list.data && !list.data.items.length ? <p className="muted">Aucun import.</p> : null}
        {list.data?.items.length ? (
          <Table caption="Imports">
            <thead>
              <tr>
                <th>Date</th>
                <th>Contenu</th>
                <th>Fichier</th>
                <th>Statut</th>
                <th className="num">Lignes</th>
                <th className="num">Importées</th>
                <th className="num">Erreurs</th>
                <th className="num">Doublons</th>
                <th>Pièces</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((i: any) => (
                <tr key={i.id}>
                  <td>{dateTime(i.created_at)}</td>
                  <td>{KIND_LABELS[i.kind]}</td>
                  <td>
                    {i.filename}
                    {i.source_tool ? <span className="block small muted">{i.source_tool}</span> : null}
                  </td>
                  <td>
                    <Status kind="importStatus" value={i.status} />
                  </td>
                  <td className="num">{i.total_rows}</td>
                  <td className="num">{i.applied_rows}</td>
                  <td className="num">{i.error_rows}</td>
                  <td className="num">{i.duplicate_rows}</td>
                  <td className="small">
                    {i.pieces_total ? `${i.pieces_attached} rattachée(s), ${i.pieces_duplicate} déjà présente(s), ${i.pieces_error} refusée(s)` : "—"}
                  </td>
                  <td className="right">
                    <Button variant="discret" small onClick={() => setOpen(i.id)}>
                      Rapport
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {open ? <ImportDetail id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function ArchiveReport({ archive }: { archive: any }) {
  const rejected: any[] = archive.rejected ?? [];
  const unused: string[] = archive.unused ?? [];
  return (
    <Notice tone={archive.error || rejected.length ? "attention" : "info"} title={`Archive ${archive.filename}`}>
      <span className="block">{archive.error ? archive.error : `${archive.files} fichier(s) lisible(s) dans l’archive.`}</span>
      {rejected.map((r) => (
        <span key={r.path} className="block small">
          Refusé : <span className="mono">{r.path}</span> — {r.message}
        </span>
      ))}
      {unused.length ? <span className="block small muted">Non cités dans le fichier, ignorés : {unused.join(", ")}.</span> : null}
    </Notice>
  );
}

function repriseText(d: any): string | null {
  if (!d || (!d.date_reprise && !d.solde_ouverture)) return null;
  const parts: string[] = [];
  if (d.date_reprise) parts.push(`Reprise le ${date(d.date_reprise)}`);
  if (d.solde_ouverture > 0) parts.push(`solde dû ${money(d.solde_ouverture)}`);
  if (d.solde_ouverture < 0) parts.push(`trop-perçu ${money(-d.solde_ouverture)}`);
  if (d.depot_detenu !== undefined && d.depot_detenu !== null) parts.push(`dépôt détenu ${money(d.depot_detenu)}`);
  return parts.join(" · ");
}

function ImportReport({ view }: { view: any }) {
  const i = view.import;
  const s = view.summary;
  const pieces = s?.pieces;
  const isLease = i.kind === "baux";
  return (
    <div className="stack">
      <div className="stats">
        <Stat label={i.status === "applique" ? "Lignes importées" : "Lignes acceptées"} value={s?.accepted ?? i.valid_rows} />
        <Stat label="Lignes refusées" value={i.error_rows} hint={i.error_rows ? "À corriger puis réimporter" : undefined} />
        <Stat label="Déjà importées" value={i.duplicate_rows} hint="Non modifiées" />
        {pieces?.total ? (
          <Stat
            label={i.status === "applique" ? "Pièces rattachées" : "Pièces à rattacher"}
            value={i.status === "applique" ? pieces.attached : pieces.to_attach}
            hint={`${pieces.duplicates} déjà présente(s), ${pieces.errors} refusée(s)`}
          />
        ) : null}
      </div>
      <p className="small muted">
        Provenance : {i.filename}
        {i.source_tool ? ` (${i.source_tool})` : ""}, déposé par {i.created_by_name ?? "—"} le {dateTime(i.created_at)}
        {i.applied_at ? `, appliqué le ${dateTime(i.applied_at)}` : ""}.
      </p>
      {view.archive?.filename ? <ArchiveReport archive={view.archive} /> : null}
      <Table caption="Rapport ligne par ligne">
        <thead>
          <tr>
            <th className="num">Ligne</th>
            <th>Référence</th>
            <th>Résultat</th>
            {isLease ? <th>Reprise</th> : null}
            <th>Détail et pièces</th>
          </tr>
        </thead>
        <tbody>
          {(view.rows as any[]).map((r) => (
            <tr key={r.line}>
              <td className="num">{r.line}</td>
              <td className="mono">{r.reference || "—"}</td>
              <td>
                <Status kind="importRow" value={r.status} />
              </td>
              {isLease ? <td className="small">{repriseText(r.data) ?? "—"}</td> : null}
              <td>
                {(r.messages as string[]).map((m) => (
                  <span key={m} className="block small">
                    {m}
                  </span>
                ))}
                {(r.warnings as string[]).map((m) => (
                  <span key={m} className="block small muted">
                    À vérifier : {m}
                  </span>
                ))}
                {((r.pieces ?? []) as any[]).map((p, n) => (
                  <span key={`${p.path}-${n}`} className="block small">
                    <Status kind="importPiece" value={p.status} /> {p.category_label} · <span className="mono">{p.path}</span>
                    {p.message ? ` — ${p.message}` : ""}
                  </span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function ImportDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const q = useGet<any>(org.api(`/pro/imports/${id}`));
  return (
    <Modal open onClose={onClose} title="Rapport d’import">
      {q.isLoading ? <Loading /> : null}
      {q.data ? <ImportReport view={q.data} /> : null}
    </Modal>
  );
}
